import React, { Fragment } from 'react';
import {
  Image,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import LogoFamima from 'shared/assets/images/settlements/familymart/logo@3x.png';
import LogoLawson from 'shared/assets/images/settlements/loppi/logo-lawson@3x.png';
import LogoMinistop from 'shared/assets/images/settlements/loppi/logo-ministop@3x.png';
import LogoSeicomart from 'shared/assets/images/settlements/seicomart/logo@3x.png';
import generateID from 'shared/modules/common/utils/generate-id';

type Settlement = 'familymart' | 'loppi' | 'seicomart' | 'transfer';

type Method = {
  logos: string[];
  labels: string[];
  value: Settlement;
};

type Props = {
  /**
   * Is settlement via Combini available
   */
  isCombiniAvailable: boolean;
  /**
   * Is settlement via Bank Transfer available
   */
  isBankTransferAvailable: boolean;
  /**
   * IF the custom VAN is unavailable, then set this to true.
   */
  isUsingFallbackVAN?: boolean;
  /**
   * Invoked when a settlement method is pressed
   */
  onItemPress?: (method: Method['value']) => void;
  style?: StyleProp<ViewStyle>;
};

const methods: Method[] = [
  {
    logos: [LogoLawson, LogoMinistop],
    labels: ['ローソンでお支払い', 'ミニストップでお支払い'],
    value: 'loppi',
  },
  {
    logos: [LogoFamima],
    labels: ['ファミリーマートでお支払い'],
    value: 'familymart',
  },
  {
    logos: [LogoSeicomart],
    labels: ['セイコーマートでお支払い'],
    value: 'seicomart',
  },
];

const SettlementMethods = (props: Props) => {
  const onPress = (method: Settlement) => () => props.onItemPress?.(method);

  return (
    <View style={props.style}>
      {props.isCombiniAvailable && (
        <>
          <Text style={style.sectionHeader}>
            ご希望のコンビニを下記よりお選びください
          </Text>
          {methods.map((method, index) => (
            <TouchableOpacity
              key={index}
              style={[
                style.sectionItem,
                index === methods.length - 1 && style.marginBottom,
              ]}
              onPress={onPress(method.value)}
              {...generateID(`settlement-method-${method.value}`)}
            >
              {method.logos.map((logo, logoIndex) => (
                <Fragment key={logoIndex}>
                  <Image source={{ uri: logo }} style={style.sectionImage} />
                  <Text style={[style.text, style.sectionText]}>
                    {method.labels[logoIndex]}
                  </Text>
                </Fragment>
              ))}
            </TouchableOpacity>
          ))}
        </>
      )}
      {props.isBankTransferAvailable && (
        <>
          <Text style={style.sectionHeader}>
            銀行振込先は下記よりご確認ください
          </Text>
          <Text style={style.sectionDescription}>
            振込口座が新しくなりました。 お客様専用振込口座の利用で、
            支払い確認がスムーズに。
          </Text>
          <TouchableOpacity
            style={[style.sectionItem, style.marginBottom]}
            onPress={onPress('transfer')}
            {...generateID('settlement-method-bank-transfer')}
          >
            <Text style={[style.text, style.textHighlighted]}>
              お客様専用振込口座
            </Text>
          </TouchableOpacity>
          <Text style={style.text}>
            銀行振込の手数料はお客様負担となります。
          </Text>
        </>
      )}
    </View>
  );
};

const style = StyleSheet.create({
  text: {
    fontSize: 12,
    color: '#586065',
    textAlign: 'center',
  },
  textHighlighted: {
    color: '#E5258C',
  },
  sectionHeader: {
    color: '#607D8B',
    fontWeight: 'bold',
    marginBottom: 16,
  },
  sectionDescription: {
    color: '#2A2E32',
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 16,
  },
  sectionItem: {
    backgroundColor: 'white',
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 72,
    borderRadius: 8,
    marginBottom: 16,
  },
  sectionImage: {
    width: 40,
    height: 40,
  },
  sectionText: {
    marginHorizontal: 8,
    width: 156,
    textAlign: 'left',
  },
  marginBottom: {
    marginBottom: 24,
  },
});

export default SettlementMethods;
